import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Avatar,
  TextField,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from './AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function AchievementModal({ open, onClose, achievement, onClaim, onUpdate }) {
  const { user, isAuthenticated, adminDisplayEnabled } = useAuth();
  const isAdmin = adminDisplayEnabled;
  const [isClaimed, setIsClaimed] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editingParScore, setEditingParScore] = useState(false);
  const [hoveringImage, setHoveringImage] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedParScore, setEditedParScore] = useState(0);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open && achievement && user) {
      checkClaimed();
    }
    if (open && achievement) {
      setEditedName(achievement.name || '');
      setEditedDescription(achievement.description || '');
      setEditedParScore(achievement.par_score || 0);
      // Only update imageUrl if it's different to preserve uploaded image
      if (achievement.image_url && achievement.image_url !== imageUrl) {
        setImageUrl(achievement.image_url || '');
      } else if (!imageUrl) {
        setImageUrl(achievement.image_url || '');
      }
    }
  }, [open, achievement, user]);

  const checkClaimed = async () => {
    if (!user || !achievement) return;
    const userId = user.id || user._id;
    if (!userId) return;
    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}/achievements`);
      const claimed = response.data.some(a => a.id === achievement.id);
      setIsClaimed(claimed);
    } catch (error) {
      console.error('Error checking claimed status:', error);
    }
  };

  const handleClaim = async () => {
    if (!isAuthenticated || !user) {
      alert('Please sign in to claim achievements');
      return;
    }

    const userId = user.id || user._id;
    if (!userId) {
      alert('User ID not found');
      return;
    }

    setClaiming(true);
    try {
      if (isClaimed) {
        await axios.delete(`${API_URL}/api/users/${userId}/achievements/${achievement.id}`);
        setIsClaimed(false);
      } else {
        await axios.post(`${API_URL}/api/users/${userId}/achievements/${achievement.id}`);
        setIsClaimed(true);
      }
      
      if (onClaim) {
        onClaim();
      }
    } catch (error) {
      console.error('Error claiming achievement:', error);
      alert('Failed to claim achievement. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  const handleSaveField = async (field, value) => {
    if (!isAdmin || !achievement) return;
    
    try {
      const updateData = {
        name: field === 'name' ? value : editedName,
        description: field === 'description' ? value : editedDescription,
        par_score: field === 'par_score' ? parseInt(value) || 0 : editedParScore,
        image_url: achievement.image_url,
        is_trap: achievement.is_trap || false,
      };
      
      await axios.put(`${API_URL}/api/achievements/${achievement.id}`, updateData);
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating achievement:', error);
      alert('Failed to update achievement. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!isAdmin || !achievement) return;
    
    const confirmMessage = `Are you sure you want to delete "${achievement.name}"? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/achievements/${achievement.id}`);
      
      if (onUpdate) {
        onUpdate();
      }
      
      onClose();
    } catch (error) {
      console.error('Error deleting achievement:', error);
      alert(`Failed to delete achievement: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleImageChange = async (e) => {
    console.log('=== handleImageChange START ===');
    console.log('handleImageChange called in modal', { 
      isAdmin, 
      achievement: achievement?.id, 
      files: e.target.files,
      filesLength: e.target.files?.length 
    });
    
    if (!isAdmin || !achievement) {
      console.log('Early return: not admin or no achievement', { isAdmin, hasAchievement: !!achievement });
      return;
    }
    
    const file = e.target.files?.[0];
    if (!file) {
      console.log('No file selected - files array:', e.target.files);
      return;
    }
    
    console.log('File selected:', { name: file.name, type: file.type, size: file.size });
    
    setUploadingImage(true);
    setUploadSuccess(false);
    
    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageUrl(reader.result);
    };
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
    };
    reader.readAsDataURL(file);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      console.log('Uploading file to:', `${API_URL}/api/upload`);
      const uploadResponse = await axios.post(`${API_URL}/api/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        timeout: 60000, // 60 second timeout
      });
      
      console.log('Upload response:', uploadResponse.data);
      
      const updateData = {
        name: editedName,
        description: editedDescription,
        par_score: editedParScore,
        image_url: uploadResponse.data.url,
        is_trap: achievement.is_trap || false,
      };
      
      console.log('Updating achievement:', achievement.id, updateData);
      const response = await axios.put(`${API_URL}/api/achievements/${achievement.id}`, updateData);
      
      // Update the image URL to the uploaded URL immediately
      const newImageUrl = uploadResponse.data.url;
      setImageUrl(newImageUrl);
      
      // Update the achievement object locally to prevent modal from closing
      if (achievement) {
        achievement.image_url = newImageUrl;
      }
      
      setUploadSuccess(true);
      
      // Call onUpdate after a short delay to allow state to settle
      if (onUpdate) {
        setTimeout(() => {
          onUpdate();
        }, 100);
      }
      
      // Clear success message after 2 seconds
      setTimeout(() => {
        setUploadSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Error updating image:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert(`Failed to update image: ${error.response?.data?.detail || error.message}`);
      // Revert to original image on error
      setImageUrl(achievement.image_url || '');
    } finally {
      setUploadingImage(false);
      // Reset file input
      e.target.value = '';
    }
  };

  if (!achievement) return null;

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        // Prevent closing during upload
        if (uploadingImage) {
          console.log('Preventing modal close during upload');
          return;
        }
        // Prevent closing when clicking outside if file picker might be open
        if (reason === 'backdropClick' && fileInputRef.current) {
          console.log('Backdrop click detected, checking if file picker is active');
          // Small delay to see if file picker opens
          setTimeout(() => {
            if (!fileInputRef.current?.files?.length) {
              onClose();
            }
          }, 100);
          return;
        }
        console.log('Modal closing, reason:', reason);
        onClose();
      }} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={uploadingImage}
    >
      <DialogTitle sx={{ p: 0, pb: 0, display: 'none' }} />
      <DialogContent sx={{ px: 0.5, pt: 3.6, pb: 1.8, position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            pt: 2.4,
            pb: 1.2,
          }}
        >
          <Box 
            sx={{ position: 'relative', width: '100%', maxWidth: 400 }}
            onMouseEnter={() => isAdmin && setHoveringImage(true)}
            onMouseLeave={() => setHoveringImage(false)}
          >
            <Box
              component="img"
              src={imageUrl || achievement.image_url}
              alt={achievement.name}
              sx={{
                width: '100%',
                height: 'auto',
                mb: 0.5,
                border: '2px solid',
                borderColor: uploadSuccess ? '#4caf50' : 'primary.main',
                borderRadius: 2,
                objectFit: 'cover',
                flexShrink: 0,
                display: 'block',
                opacity: uploadingImage ? 0.6 : 1,
                transition: 'all 0.3s ease',
              }}
            />
            {uploadingImage && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={40} sx={{ color: 'white' }} />
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                  Uploading...
                </Typography>
              </Box>
            )}
            {uploadSuccess && !uploadingImage && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 3,
                  backgroundColor: '#4caf50',
                  color: 'white',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                ✓ Saved
              </Box>
            )}
            {isAdmin && hoveringImage && !uploadingImage && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 2,
                }}
              >
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="image-upload"
                  type="file"
                  ref={(input) => {
                    fileInputRef.current = input;
                    if (input) {
                      // Ensure onChange is attached directly
                      input.onchange = (e) => {
                        console.log('Direct onchange handler fired!', e.target.files);
                        handleImageChange(e);
                      };
                      console.log('File input ref set, onChange attached');
                    }
                  }}
                  onChange={(e) => {
                    console.log('React onChange handler fired!', e.target.files);
                    try {
                      handleImageChange(e);
                    } catch (error) {
                      console.error('Error in onChange handler:', error);
                    }
                  }}
                  onClick={(e) => {
                    console.log('File input clicked');
                    e.stopPropagation();
                  }}
                />
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    console.log('Edit icon clicked in modal, triggering file input');
                    console.log('File input ref:', fileInputRef.current);
                    
                    // Use setTimeout to ensure the click happens after any state updates
                    setTimeout(() => {
                      if (fileInputRef.current) {
                        try {
                          console.log('About to trigger file input click');
                          fileInputRef.current.click();
                          console.log('File input click() called successfully');
                          
                          // Add a listener to catch the change event
                          const checkForFile = () => {
                            if (fileInputRef.current?.files?.length > 0) {
                              console.log('File detected via polling:', fileInputRef.current.files[0].name);
                              const syntheticEvent = {
                                target: fileInputRef.current,
                                preventDefault: () => {},
                                stopPropagation: () => {}
                              };
                              handleImageChange(syntheticEvent);
                            } else {
                              setTimeout(checkForFile, 100);
                            }
                          };
                          setTimeout(checkForFile, 200);
                        } catch (error) {
                          console.error('Error triggering file input:', error);
                        }
                      } else {
                        console.error('File input ref is null - input not mounted yet');
                        // Fallback to getElementById
                        const fileInput = document.getElementById('image-upload');
                        if (fileInput) {
                          console.log('Found file input via getElementById, triggering click');
                          fileInput.click();
                        } else {
                          console.error('File input not found via getElementById either');
                        }
                      }
                    }, 0);
                  }}
                  sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    },
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            )}
            {isAdmin && (
              <IconButton 
                onClick={handleDelete} 
                size="small" 
                sx={{ 
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  backgroundColor: 'rgba(244, 67, 54, 0.8)',
                  color: 'white',
                  zIndex: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(244, 67, 54, 1)',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton 
              onClick={onClose} 
              size="small" 
              sx={{ 
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                zIndex: 1,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {isAdmin && editingName ? (
            <TextField
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onBlur={() => {
                setEditingName(false);
                handleSaveField('name', editedName);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setEditingName(false);
                  handleSaveField('name', editedName);
                }
                if (e.key === 'Escape') {
                  setEditingName(false);
                  setEditedName(achievement.name);
                }
              }}
              autoFocus
              variant="standard"
              sx={{
                mt: 0.5,
                mb: 0.5,
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  fontSize: '1.5rem',
                },
              }}
            />
          ) : (
            <Typography 
              variant="h5" 
              onClick={() => isAdmin && setEditingName(true)}
              sx={{ 
                fontWeight: 600, 
                mb: 0.5,
                mt: 0.5,
                textTransform: 'uppercase',
                cursor: isAdmin ? 'pointer' : 'default',
                '&:hover': isAdmin ? {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 1,
                } : {},
              }}
            >
              {achievement.name}
            </Typography>
          )}
          {isAdmin && editingDescription ? (
            <TextField
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              onBlur={() => {
                setEditingDescription(false);
                handleSaveField('description', editedDescription);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setEditingDescription(false);
                  handleSaveField('description', editedDescription);
                }
                if (e.key === 'Escape') {
                  setEditingDescription(false);
                  setEditedDescription(achievement.description);
                }
              }}
              autoFocus
              multiline
              variant="standard"
              sx={{
                mb: 0.5,
                px: 1,
                width: '100%',
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                },
              }}
            />
          ) : (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              onClick={() => isAdmin && setEditingDescription(true)}
              sx={{ 
                mb: 0.5,
                px: 1,
                cursor: isAdmin ? 'pointer' : 'default',
                '&:hover': isAdmin ? {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 1,
                } : {},
              }}
            >
              {achievement.description}
            </Typography>
          )}
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mt: 'auto', mb: 0.6 }}
          >
            {isClaimed && isAuthenticated ? (
              <>
                <Chip
                  label={`${achievement.par_score >= 0 ? '+' : ''}${achievement.par_score} points`}
                  size="small"
                  sx={{
                    backgroundColor: achievement.par_score < 0 ? '#4caf50' : '#667eea',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                  }}
                />
                <Chip
                  label="Unclaim"
                  onClick={handleClaim}
                  disabled={claiming}
                  size="small"
                  sx={{
                    backgroundColor: '#f44336',
                    color: 'white',
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#d32f2f',
                    },
                  }}
                />
              </>
            ) : (isAuthenticated && !isClaimed) || (isAdmin && !isClaimed) ? (
              <>
                {isAdmin && editingParScore ? (
                  <TextField
                    type="number"
                    value={editedParScore}
                    onChange={(e) => setEditedParScore(parseInt(e.target.value) || 0)}
                    onBlur={() => {
                      setEditingParScore(false);
                      handleSaveField('par_score', editedParScore);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setEditingParScore(false);
                        handleSaveField('par_score', editedParScore);
                      }
                      if (e.key === 'Escape') {
                        setEditingParScore(false);
                        setEditedParScore(achievement.par_score);
                      }
                    }}
                    autoFocus
                    variant="standard"
                    inputProps={{ min: -999, max: 999 }}
                    sx={{
                      width: 80,
                      '& .MuiInputBase-input': {
                        textAlign: 'center',
                        fontWeight: 600,
                      },
                    }}
                  />
                ) : (
                  <Chip
                    label={`Par: ${achievement.par_score}`}
                    onClick={() => isAdmin && setEditingParScore(true)}
                    size="small"
                    color="primary"
                    sx={{ 
                      fontWeight: 600,
                      cursor: isAdmin ? 'pointer' : 'default',
                    }}
                  />
                )}
                <Chip
                  label="Claim"
                  onClick={handleClaim}
                  disabled={claiming}
                  size="small"
                  sx={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    fontWeight: 600,
                    cursor: 'pointer',
                    px: '5px',
                    '&:hover': {
                      backgroundColor: '#45a049',
                    },
                  }}
                />
              </>
            ) : (
              isAdmin && editingParScore ? (
                <TextField
                  type="number"
                  value={editedParScore}
                  onChange={(e) => setEditedParScore(parseInt(e.target.value) || 0)}
                  onBlur={() => {
                    setEditingParScore(false);
                    handleSaveField('par_score', editedParScore);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setEditingParScore(false);
                      handleSaveField('par_score', editedParScore);
                    }
                    if (e.key === 'Escape') {
                      setEditingParScore(false);
                      setEditedParScore(achievement.par_score);
                    }
                  }}
                  autoFocus
                  variant="standard"
                  inputProps={{ min: -999, max: 999 }}
                  sx={{
                    width: 80,
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                      fontWeight: 600,
                    },
                  }}
                />
              ) : (
                <Chip
                  label={`Par: ${achievement.par_score}`}
                  onClick={() => isAdmin && setEditingParScore(true)}
                  size="small"
                  color="primary"
                  sx={{ 
                    fontWeight: 600,
                    cursor: isAdmin ? 'pointer' : 'default',
                  }}
                />
              )
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default AchievementModal;

